import React, { useEffect, useRef } from 'react';
import { ICON } from '../icons/iconIds';
import IconText from './icons/IconText';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import './DeviationWizardMap.css';

// Fix Leaflet default markers
delete L.Icon.Default.prototype._getIconUrl;
L.Icon.Default.mergeOptions({
  iconRetinaUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon-2x.png',
  iconUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-icon.png',
  shadowUrl: 'https://cdnjs.cloudflare.com/ajax/libs/leaflet/1.9.4/images/marker-shadow.png',
});

const DeviationWizardMap = ({
  collarPoint,
  searchPolygon,
  neighbors,
  radiusUsed,
  nPairs,
  metadata,
  onPointSelect
}) => {
  const mapRef = useRef(null);
  const mapInstanceRef = useRef(null);
  const layersRef = useRef([]);
  const markerRef = useRef(null);

  // Initialize map
  useEffect(() => {
    if (mapRef.current && !mapInstanceRef.current) {
      const map = L.map(mapRef.current, {
        center: [62.5, -137.2],
        zoom: 10,
        zoomControl: true
      });

      // Add satellite base layer
      const satelliteMap = L.tileLayer('https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}', {
        attribution: '© Esri',
        maxZoom: 18
      });

      satelliteMap.addTo(map);

      // Add layer control
      const baseLayers = {
        "Satellite": satelliteMap,
        "Street": L.tileLayer('https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png', {
          attribution: '© OpenStreetMap'
        })
      };

      L.control.layers(baseLayers, {}, { position: 'topleft' }).addTo(map);

      // Add scale
      L.control.scale({ position: 'bottomleft' }).addTo(map);

      // Add click handler for map to place new point
      map.on('click', (e) => {
        if (onPointSelect) {
          onPointSelect(e.latlng.lat, e.latlng.lng);
        }
      });

      mapInstanceRef.current = map;
    }

    return () => {
      if (mapInstanceRef.current) {
        mapInstanceRef.current.remove();
        mapInstanceRef.current = null;
      }
    };
  }, []);

  // Add collar point, search polygon, and neighbors
  useEffect(() => {
    if (!mapInstanceRef.current || !collarPoint) return;

    // Clear existing layers
    layersRef.current.forEach(layer => layer.remove());
    layersRef.current = [];

    // Add search polygon if available
    if (searchPolygon && searchPolygon.coordinates) {
      const polygonLayer = L.geoJSON(searchPolygon, {
        style: {
          color: '#3b82f6',
          fillColor: '#3b82f6',
          fillOpacity: 0.2,
          weight: 2
        }
      }).addTo(mapInstanceRef.current);
      layersRef.current.push(polygonLayer);
    }

    // Add neighbor collar points if available
    if (neighbors) {
      // Helper function to get color based on weight
      const getWeightColor = (weight) => {
        if (!weight || weight <= 0) return '#6b7280'; // gray (no weight / not used)
        if (weight <= 0.2) return '#10b981'; // green
        if (weight <= 0.4) return '#fbbf24'; // yellow
        if (weight <= 0.6) return '#f97316'; // orange
        if (weight <= 0.8) return '#ef4444'; // red
        return '#ec4899'; // magenta (high weight)
      };

      const neighborLayer = L.geoJSON(neighbors, {
        pointToLayer: (feature, latlng) => {
          const weight = feature.properties?.w_total || 0;
          const color = getWeightColor(weight);
          
          const neighborIcon = L.divIcon({
            className: 'neighbor-marker',
            html: `<div class="neighbor-dot" style="background-color: ${color}; border-color: white;"></div>`,
            iconSize: [10, 10],
            iconAnchor: [5, 5]
          });
          
          return L.marker(latlng, {
            icon: neighborIcon,
            zIndexOffset: 500
          });
        },
        onEachFeature: (feature, layer) => {
          if (feature.properties) {
            const props = feature.properties;
            const weight = props.w_total || 0;
            const weightColor = getWeightColor(weight);
            const isUsed = weight > 0;
            
            layer.bindPopup(`
              <div style="font-family: sans-serif; min-width: 180px;">
                <strong style="color: #1f2937; font-size: 12px;">Historical Collar ${!isUsed ? '(Not Used)' : ''}</strong>
                <div style="margin-top: 6px; font-size: 11px; color: #4b5563;">
                  ${props.hole_id ? `<div><strong>Hole ID:</strong> ${props.hole_id}</div>` : ''}
                  ${props.az_start !== undefined ? `<div><strong>Azimuth:</strong> ${parseFloat(props.az_start).toFixed(1)}°</div>` : ''}
                  ${props.dip_start !== undefined ? `<div><strong>Dip:</strong> ${parseFloat(props.dip_start).toFixed(1)}°</div>` : ''}
                  ${props.dist_m ? `<div><strong>Distance:</strong> ${parseFloat(props.dist_m).toFixed(1)} m</div>` : ''}
                  <div style="margin-top: 8px; padding-top: 8px; border-top: 1px solid #e5e7eb;">
                    <div><strong>Weight:</strong> <span style="color: ${weightColor}; font-weight: bold;">${weight.toFixed(3)}</span> ${!isUsed ? '(excluded)' : ''}</div>
                    ${isUsed && props.w_dir !== undefined ? `<div><strong>Direction W:</strong> ${parseFloat(props.w_dir).toFixed(3)}</div>` : ''}
                    ${isUsed && props.w_dist !== undefined ? `<div><strong>Distance W:</strong> ${parseFloat(props.w_dist).toFixed(3)}</div>` : ''}
                  </div>
                  ${props.delta_az_deg ? `<div style="margin-top: 4px;"><strong>Δ Az:</strong> ${parseFloat(props.delta_az_deg).toFixed(2)}°</div>` : ''}
                  ${props.delta_dip ? `<div><strong>Δ Dip:</strong> ${parseFloat(props.delta_dip).toFixed(2)}°</div>` : ''}
                </div>
              </div>
            `);
          }
        }
      }).addTo(mapInstanceRef.current);
      layersRef.current.push(neighborLayer);
      
      // Add azimuth direction lines for each neighbor
      neighbors.features.forEach(feature => {
        if (feature.geometry.type === 'Point' && feature.properties?.az_start !== undefined) {
          const [lon, lat] = feature.geometry.coordinates;
          const azimuth = feature.properties.az_start;
          const weight = feature.properties.w_total || 0;
          const color = getWeightColor(weight);
          const lineLength = 50; // meters
          
          // Convert azimuth to end point
          // Azimuth: 0° = North, 90° = East (clockwise from North)
          const mathAngle = 90 - azimuth; // Convert to mathematical angle
          const angleRad = mathAngle * Math.PI / 180;
          
          // Calculate end point
          const metersPerDegreeLat = 111320;
          const metersPerDegreeLon = metersPerDegreeLat * Math.cos(lat * Math.PI / 180);
          
          const deltaLat = (lineLength * Math.sin(angleRad)) / metersPerDegreeLat;
          const deltaLon = (lineLength * Math.cos(angleRad)) / metersPerDegreeLon;
          
          const endPoint = L.latLng(lat + deltaLat, lon + deltaLon);
          
          const directionLine = L.polyline(
            [[lat, lon], [endPoint.lat, endPoint.lng]],
            {
              color: color,
              weight: 2,
              opacity: 0.8,
              className: 'neighbor-direction-line'
            }
          ).addTo(mapInstanceRef.current);
          
          layersRef.current.push(directionLine);
        }
      });
    }

    // Add directional sector overlay (before marker so it's below)
    if (collarPoint && collarPoint.coordinates && metadata) {
      const [lon, lat] = collarPoint.coordinates;
      const azimuth = metadata.az_in || metadata.input?.az_deg || 0;
      const searchRadius = radiusUsed || 500;
      const sectorRadiusMeters = searchRadius * 0.3; // 30% of search radius for visualization
      
      // Create sector using Leaflet semicircle or canvas
      // Using a simple polygon approximation
      const centerPoint = L.latLng(lat, lon);
      const sectorPoints = [centerPoint];
      
      // Generate arc points
      // Azimuth: 0° = North, 90° = East, 180° = South, 270° = West (clockwise from North)
      // We need to convert to mathematical angle: 0° = East, 90° = North (counter-clockwise)
      // Mathematical angle = 90 - azimuth
      const arcSpan = 90; // ±45° from center
      const numPoints = 20;
      
      for (let i = 0; i <= numPoints; i++) {
        const azimuthOffset = (i / numPoints - 0.5) * arcSpan; // -45 to +45
        const pointAzimuth = azimuth + azimuthOffset;
        
        // Convert azimuth to mathematical radians
        // Azimuth 0° (North) should point up, 90° (East) should point right
        // Mathematical: 0° = right (East), 90° = up (North)
        // So: mathAngle = 90° - azimuth
        const mathAngle = 90 - pointAzimuth;
        const angleRad = mathAngle * Math.PI / 180;
        
        // Calculate point using trigonometry
        // Approximate meters to degrees (rough, works for small distances)
        const metersPerDegreeLat = 111320; // meters per degree latitude
        const metersPerDegreeLon = metersPerDegreeLat * Math.cos(lat * Math.PI / 180);
        
        const deltaLat = (sectorRadiusMeters * Math.sin(angleRad)) / metersPerDegreeLat;
        const deltaLon = (sectorRadiusMeters * Math.cos(angleRad)) / metersPerDegreeLon;
        
        sectorPoints.push(L.latLng(lat + deltaLat, lon + deltaLon));
      }
      
      sectorPoints.push(centerPoint); // Close the sector
      
      const sectorLayer = L.polygon(sectorPoints, {
        color: '#3b82f6',
        fillColor: '#3b82f6',
        fillOpacity: 0.15,
        weight: 2,
        className: 'directional-sector'
      }).addTo(mapInstanceRef.current);
      
      layersRef.current.push(sectorLayer);
    }

    // Add collar point marker
    if (collarPoint && collarPoint.coordinates) {
      const [lon, lat] = collarPoint.coordinates;
      
      // Get UTM coordinates from metadata or calculate
      const easting = metadata?.easting || metadata?.input?.easting;
      const northing = metadata?.northing || metadata?.input?.northing;
      const azimuth = metadata?.az_in || metadata?.input?.az_deg;
      
      // Create custom blue pulsing marker
      const collarIcon = L.divIcon({
        className: 'collar-marker',
        html: `<div class="collar-marker-wrapper">
          <div class="collar-pulse-ring"></div>
          <div class="collar-marker-dot"></div>
        </div>`,
        iconSize: [40, 40],
        iconAnchor: [20, 20]
      });

      const markerLayer = L.marker([lat, lon], {
        icon: collarIcon,
        draggable: true,
        zIndexOffset: 1000
      }).addTo(mapInstanceRef.current);

      markerLayer.bindPopup(`
        <div style="font-family: sans-serif;">
          <strong style="color: #1f2937; font-size: 14px;">Test Collar Location</strong>
          <div style="margin-top: 8px; font-size: 12px; color: #4b5563;">
            <div><strong>Easting:</strong> ${easting ? easting.toFixed(2) : 'N/A'} m</div>
            <div><strong>Northing:</strong> ${northing ? northing.toFixed(2) : 'N/A'} m</div>
            ${azimuth !== undefined ? `<div><strong>Azimuth:</strong> ${azimuth.toFixed(1)}°</div>` : ''}
            <div style="margin-top: 4px; font-size: 10px; color: #9ca3af;">(Drag marker to recalculate)</div>
          </div>
        </div>
      `);

      // Handle marker drag
      markerLayer.on('dragend', (e) => {
        const newLatLng = e.target.getLatLng();
        if (onPointSelect) {
          onPointSelect(newLatLng.lat, newLatLng.lng);
        }
      });

      markerRef.current = markerLayer;
      layersRef.current.push(markerLayer);

      // Fit bounds to show collar and polygon
      const bounds = [];
      bounds.push([lat, lon]);
      
      if (searchPolygon && searchPolygon.coordinates && searchPolygon.coordinates[0]) {
        searchPolygon.coordinates[0].forEach(coord => {
          bounds.push([coord[1], coord[0]]);
        });
      }

      if (bounds.length > 0) {
        mapInstanceRef.current.fitBounds(bounds, { padding: [50, 50] });
      }
    }
  }, [collarPoint, searchPolygon, neighbors]);

  return (
    <div className="deviation-wizard-map-container">
      <div ref={mapRef} className="deviation-wizard-map" style={{ height: '400px', width: '100%', borderRadius: '8px' }} />
      
      {/* Map Info Panel */}
      <div className="map-info-panel">
        <IconText icon={ICON.uiLocationPin} as="h4">Search Area Details</IconText>
        <div className="info-grid">
          <div className="info-item">
            <span className="info-label">Radius Used:</span>
            <span className="info-value">{radiusUsed ? `${radiusUsed.toFixed(0)}m` : 'N/A'}</span>
          </div>
          <div className="info-item">
            <span className="info-label">Historical Pairs:</span>
            <span className="info-value">{nPairs || 0}</span>
          </div>
          {metadata?.dip_bin_key && (
            <div className="info-item">
              <span className="info-label">Dip Bin:</span>
              <span className="info-value">{metadata.dip_bin_key}</span>
            </div>
          )}
          {metadata?.depth_bin_key && (
            <div className="info-item">
              <span className="info-label">Depth Bin:</span>
              <span className="info-value">{metadata.depth_bin_key}</span>
            </div>
          )}
        </div>
        
        <div className="legend">
          <div className="legend-item">
            <div className="legend-icon collar-icon"></div>
            <span>Test Collar</span>
          </div>
          <div className="legend-item">
            <div className="legend-icon sector-icon"></div>
            <span>Drilling Direction</span>
          </div>
          <div className="legend-item legend-gradient-item">
            <div className="legend-gradient-bar">
              <div className="gradient-color" style={{background: 'linear-gradient(to right, #10b981, #fbbf24, #f97316, #ef4444, #ec4899)'}}></div>
            </div>
            <span className="legend-gradient-label">Historical Holes ({nPairs || 0}) - Weight</span>
            <div className="gradient-labels">
              <span>Low</span>
              <span>High</span>
            </div>
          </div>
          <div className="legend-item">
            <div className="legend-icon polygon-icon"></div>
            <span>Search Area</span>
          </div>
        </div>
      </div>
    </div>
  );
};

export default DeviationWizardMap;
